import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const CACHE_KEY = 'admin_favorite_menus';
const CACHE_TIMESTAMP_KEY = 'admin_favorite_menus_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useFavoriteMenus = () => {
  const [favoriteMenus, setFavoriteMenus] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Load favorites from cache or database
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        // Try to load from cache first
        const cached = localStorage.getItem(CACHE_KEY);
        const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        
        if (cached && timestamp) {
          const age = Date.now() - parseInt(timestamp);
          if (age < CACHE_DURATION) {
            setFavoriteMenus(JSON.parse(cached));
            setLoading(false);
            return;
          }
        }

        // Load from database
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('user_preferences')
          .select('favorite_admin_menus')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading favorite menus:', error);
          setLoading(false);
          return;
        }

        const favorites = Array.isArray(data?.favorite_admin_menus) 
          ? data.favorite_admin_menus as string[]
          : [];
        setFavoriteMenus(favorites);
        
        // Update cache
        localStorage.setItem(CACHE_KEY, JSON.stringify(favorites));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (error) {
        console.error('Error in loadFavorites:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, []);

  // Toggle favorite menu
  const toggleFavoriteMenu = useCallback(async (url: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const isFav = favoriteMenus.includes(url);
      let newFavorites: string[];

      if (isFav) {
        // Remove from favorites
        newFavorites = favoriteMenus.filter(menu => menu !== url);
        toast({
          title: "ลบออกจากเมนูโปรด",
          description: "เมนูถูกลบออกจากรายการโปรดแล้ว",
          variant: "default"
        });
      } else {
        // Add to favorites (limit to 10)
        if (favoriteMenus.length >= 10) {
          toast({
            title: "เมนูโปรดเต็มแล้ว",
            description: "คุณสามารถมีเมนูโปรดได้สูงสุด 10 รายการ",
            variant: "warning"
          });
          return;
        }
        newFavorites = [url, ...favoriteMenus];
        toast({
          title: "เพิ่มเมนูโปรดแล้ว",
          description: "เมนูถูกเพิ่มในรายการโปรดแล้ว",
          variant: "success"
        });
      }

      // Optimistic update
      setFavoriteMenus(newFavorites);
      
      // Update cache
      localStorage.setItem(CACHE_KEY, JSON.stringify(newFavorites));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

      // Update database
      const { error } = await supabase
        .from('user_preferences')
        .update({ favorite_admin_menus: newFavorites })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating favorite menus:', error);
        // Revert optimistic update
        setFavoriteMenus(favoriteMenus);
        toast({
          title: "เกิดข้อผิดพลาด",
          description: "ไม่สามารถบันทึกการเปลี่ยนแปลงได้",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error in toggleFavoriteMenu:', error);
    }
  }, [favoriteMenus]);

  // Check if menu is favorite
  const isFavorite = useCallback((url: string) => {
    return favoriteMenus.includes(url);
  }, [favoriteMenus]);

  return {
    favoriteMenus,
    loading,
    toggleFavoriteMenu,
    isFavorite
  };
};
